import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import moment from 'moment';
import { useAuthContext } from '../../Context/AuthContext/AuthContext';
import { useIdCardDraftQuery } from './api/idCardEndPoint';
import CommonOfflinePaymentInfo from '../new-member-applications/IndexFile/paymentInformation/offline/CommonOfflinePaymentInfo';
import axios from 'axios';
import { url } from '../../Helpers/utils/Constant';
import { getToken } from '../../rest/utils/get-token';
import { showToast } from '../../Toaster/Toaster';
import ImageUploaderWithPreview from '../../Common/ImageUploaderWithPreview';
import { API_ENDPOINTS } from '../../rest/utils/api-endpoints';

type IProps = {
  handleClose: () => void;
  setReload?: Dispatch<SetStateAction<boolean>>;
};

const IdCardForm = ({ handleClose, setReload }: IProps) => {
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const { data: idCardDraftList, fetchData: fetchIdCardDraft } =
    useIdCardDraftQuery();
  const draftData = idCardDraftList?.[0];

  useEffect(() => {
    if (user?.user_member_id) {
      fetchIdCardDraft({
        user_member_id: user.user_member_id,
        status: 'draft',
      });
    }
  }, [user?.user_member_id, fetchIdCardDraft]);

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    formState: { errors },
    setValue,
    control,
    watch,
    getValues,
  } = useForm<any>({
    defaultValues: {
      bank_ac_no: '00890320000141',
      amount: '',
      type: 'online',
      bank_name: 'Trust Bank (Kakrail Branch)',
    },
  });

  const amount = watch('amount');
  const [bloodGroup, setBloodGroup] = useState<string | undefined>('');
  const [loading, setLoading] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const token = getToken();

  useEffect(() => {
    axios
      .get(`${url}/api/atab/payment-amount?application_type=ID_CARD`)
      .then((res) => {
        if (res.data.success) {
          setValue('amount', res.data.data.amount);
        }
      });
  }, [setValue]);

  useEffect(() => {
    if (draftData) {
      if (draftData.applicant_name) {
        setValue('applicant_name', draftData.applicant_name);
      }
      if (draftData.designation) {
        setValue('designation', draftData.designation);
      }
      if (draftData.date_of_birth) {
        setValue(
          'date_of_birth',
          moment(draftData.date_of_birth).format('YYYY-MM-DD'),
        );
      }
      if (draftData.blood_group) {
        setBloodGroup(draftData.blood_group);
      }
      if (draftData.telephone) {
        setValue('telephone', draftData.telephone);
      }
      if (draftData.cellphone) {
        setValue('cellphone', draftData.cellphone);
      }
      if (draftData.email) {
        setValue('email', draftData.email);
      }
    }
  }, [draftData, setValue]);

  const onSubmit = async (data: any, asDraft: boolean = false) => {
    data.user_member_id = user.user_member_id;
    data.blood_group = bloodGroup;

    const formData = new FormData();

    const paymentFields = [
      'type',
      'bank_ac_no',
      'amount',
      'bank_name',
      'deposit_date',
      'branch_name',
      'reference_no',
      'bank_slip',
    ];

    if (asDraft) {
      formData.append('status', 'draft');
    } else {
      if (data?.type === 'online') {
        formData.append(
          'success_page',
          `${window.location.origin}/payment-success`,
        );
        formData.append(
          'failed_page',
          `${window.location.origin}/payment-failed`,
        );
        formData.append(
          'cancelled_page',
          `${window.location.origin}/payment-reject`,
        );
      }
    }

    Object.keys(data).forEach((item) => {
      if (asDraft && paymentFields.includes(item)) {
        return;
      }
      if (!asDraft && data?.type !== 'offline' && paymentFields.includes(item) && item !== 'type') {
        return;
      }

      if (typeof data[item] === 'object' && data[item] !== null) {
        if (data[item].length > 0) {
          formData.append(item, data[item][0]);
        }
      } else if (
        data[item] !== undefined &&
        data[item] !== null &&
        data[item] !== ''
      ) {
        formData.append(item, data[item]);
      }
    });

    try {
      setLoading(true);
      setIsDraft(asDraft);
      const response = await fetch(`${url}/api/member/id-card/application`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || ''}`,
        },
        body: formData,
      });

      const responseData = await response.json();

      if (responseData.success) {
        if (!asDraft && responseData?.data?.redirect_url) {
          window.open(
            responseData?.data?.redirect_url,
            '_blank',
            'noopener,noreferrer',
          );
        }
        showToast(
          responseData.message ||
          (asDraft
            ? 'Application saved as draft successfully'
            : 'Application submitted successfully'),
          'success',
        );
        queryClient.invalidateQueries([API_ENDPOINTS.ALL_ID_CARD]);
        handleClose();
        setReload?.((prev: boolean) => !prev);
      } else {
        setLoading(false);
        setIsDraft(false);
        showToast(
          responseData.message ||
          (asDraft
            ? 'Failed to save application as draft'
            : 'Failed to submit application'),
          'error',
        );
      }
    } catch (error: any) {
      showToast(error?.message || 'An error occurred', 'error');
      setLoading(false);
      setIsDraft(false);
    }
  };

  const handleSaveDraft = () => {
    const values = getValues();
    onSubmit(values, true);
  };

  return (
    <Box
      component='form'
      onSubmit={handleSubmit((data) => onSubmit(data, false))}
      p={2}
    >
      <Grid container spacing={2} mb={2}>
        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            label='Name of applicant'
            required
            id='outlined-applicant-name'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('applicant_name')}
          />
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            label='Designation'
            id='outlined-designation'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('designation')}
          />
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            required
            id='outlined-dob'
            type='date'
            label='Date of birth'
            InputLabelProps={{
              shrink: true,
            }}
            {...register('date_of_birth')}
          />
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          <FormControl fullWidth>
            <InputLabel id='idcard-blood-group-label'>Blood group</InputLabel>
            <Select
              labelId='idcard-blood-group-label'
              id='idcard-blood-group-select'
              value={bloodGroup || ''}
              onChange={(e) => setBloodGroup(e.target.value)}
              label='Blood group'
            >
              <MenuItem value='a+'>A+</MenuItem>
              <MenuItem value='a-'>A-</MenuItem>
              <MenuItem value='ab+'>AB+</MenuItem>
              <MenuItem value='ab-'>AB-</MenuItem>
              <MenuItem value='b+'>B+</MenuItem>
              <MenuItem value='b-'>B-</MenuItem>
              <MenuItem value='o+'>O+</MenuItem>
              <MenuItem value='o-'>O-</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            label='Telephone number'
            required
            id='outlined-telephone'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('telephone')}
          />
        </Grid>
        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            label='Cellphone number'
            required
            id='outlined-cellphone'
            type='text'
            InputLabelProps={{ shrink: true }}
            {...register('cellphone')}
          />
        </Grid>

        <Grid xs={12} md={4} lg={4} item>
          <TextField
            fullWidth
            label='Enter your email'
            required
            id='outlined-email'
            type='email'
            InputLabelProps={{ shrink: true }}
            {...register('email')}
          />
        </Grid>

        <Grid xs={12} md={4} lg={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Photocopy of NID (Both side)'
            fileName='member_nid_file'
            required
            imgSerUrl={draftData?.member_nid_file || null}
          />
        </Grid>

        <Grid xs={12} md={4} lg={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='One copy passport size photo'
            fileName='photo'
            required
            imgSerUrl={draftData?.photo || null}
          />
        </Grid>

        <Grid xs={12} md={4} lg={4} item>
          <ImageUploaderWithPreview
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            errors={errors}
            label='Signature with Seal'
            fileName='signature_file'
            required
            imgSerUrl={draftData?.signature_file || null}
          />
        </Grid>

        <Grid xs={12} item sx={{ my: 2 }}>
          <Divider>
            <Chip label='Payment Information' size='small' />
          </Divider>
        </Grid>
        <Grid xs={12} item>
          <CommonOfflinePaymentInfo
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            register={register}
            setValue={setValue}
            md={4}
            lg={4}
            control={control}
            onlinePaymentAmount={amount}
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        spacing={2}
        justifyContent='flex-end'
        alignItems='center'
        mt={4}
      >
        <Button
          type='button'
          variant='outlined'
          color='primary'
          disabled={loading}
          onClick={handleSaveDraft}
          startIcon={
            loading && isDraft ? (
              <CircularProgress size={18} color='inherit' />
            ) : null
          }
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: 160,
            py: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          <span>{loading && isDraft ? 'Saving Draft...' : 'Save As Draft'}</span>
        </Button>

        <Button
          type='submit'
          variant='contained'
          color='primary'
          disabled={loading}
          disableElevation
          startIcon={
            loading && !isDraft ? (
              <CircularProgress size={18} color='inherit' />
            ) : null
          }
          sx={{
            width: { xs: '100%', sm: 'auto' },
            minWidth: 160,
            py: 1,
            fontWeight: 600,
            fontSize: '0.95rem',
          }}
        >
          <span>{loading && !isDraft ? 'Submitting...' : 'Submit'}</span>
        </Button>
      </Stack>
    </Box>
  );
};

export default IdCardForm;
